import locales from "../_constants/locales";

const DEFAULT_STORE = "CAEN";
const DEFAULT_LANGUAGE = "EN";

const COUNTRY_DEFAULT_LANGUAGE = {
    CA: "EN",
    NZ: "EN",
    SG: "EN",
    AU: "EN",
    US: "EN",
    GB: "EN",
    UK: "EN",
    DE: "DE",
    ES: "ES",
    FR: "FR",
    IT: "IT",
    PT: "PT",
    BE: "FR",
    NL: "NL",
    CZ: "CS",
    FI: "EN",
    SE: "SE",
    DK: "EN",
    IE: "EN",
    PL: "PL",
    RO: "RO",
    EU: "EN"
};

const STORE_TO_COUNTRY = {
    KB: "NZ",
    NZ: "NZ",
    SG: "SG",
    AU: "AU",
    UK: "GB",
    US: "US",
    CAEN: "CA",
    CAFR: "CA",
    DE: "DE",
    ES: "ES",
    FR: "FR",
    IT: "IT",
    PT: "PT",
    BE: "BE",
    NL: "NL",
    EU: "EU",
    CORPORATE: "CA",
    RKBTEST: "CA"
};

const COUNTRY_TO_STORES = {
    NZ: ["NZ", "KB"],
    SG: ["SG", "KB"],
    AU: ["AU"],
    CA: ["CAFR", "CAEN", "KB"],
    US: ["US", "KB"],
    GB: ["UK"],
    UK: ["UK"],
    DE: ["DE", "EU"],
    ES: ["ES"],
    FR: ["FR"],
    IT: ["IT"],
    PT: ["PT"],
    BE: ["BE"],
    NL: ["NL"],
    CZ: ["EU"],
    FI: ["EU"],
    SE: ["EU"],
    DK: ["EU"],
    IE: ["EU"],
    PL: ["EU"],
    RO: ["EU"],
    EU: ["EU"]
};

function normalizeToken(value) {
    if (!value) {
        return null;
    }

    try {
        return decodeURIComponent(value).trim().toUpperCase();
    } catch (e) {
        return value.trim().toUpperCase();
    }
}

function normalizeLanguageToken(value) {
    const token = normalizeToken(value);

    if (!token) {
        return null;
    }

    return token.split(/[-_]/)[0];
}

function unique(values) {
    return [...new Set(values.filter(Boolean))];
}

function hasAnyLocaleInput({ query, store, language, country }) {
    const hasQueryLocale = query.stores.length > 0 || Boolean(query.language) || Boolean(query.country);
    const hasPropStore = Array.isArray(store) ? store.length > 0 : Boolean(store);

    return hasQueryLocale || hasPropStore || Boolean(language) || Boolean(country);
}

export function parseLocaleQuery(search) {
    if (!search) {
        return { stores: [], language: null, country: null };
    }

    const params = search.replace(/^\?/, "").split("&");
    const stores = [];
    let language = null;
    let country = null;

    params.forEach(param => {
        const [key, value] = param.split("=");
        const upperKey = normalizeToken(key);
        const upperValue = normalizeToken(value);

        if (!upperKey || !upperValue) {
            return;
        }

        if (upperKey === "STORE") {
            upperValue.split(",").forEach(store => {
                const normalizedStore = normalizeToken(store);
                if (normalizedStore) {
                    stores.push(normalizedStore);
                }
            });
        }

        if (upperKey === "LANGUAGE") {
            language = upperValue;
        }

        if (upperKey === "COUNTRY") {
            country = upperValue;
        }
    });

    return {
        stores: unique(stores),
        language,
        country
    };
}

function getCountryStores(country, language) {
    if (!country) {
        return [];
    }

    const stores = COUNTRY_TO_STORES[country] || [];

    if (country === "CA" && language === "FR") {
        return ["CAFR", ...stores.filter(store => store !== "CAFR")];
    }

    if (country === "CA" && language === "EN") {
        return ["CAEN", ...stores.filter(store => store !== "CAEN")];
    }

    return stores;
}

function filterStoresByList(stores, storeList) {
    if (!storeList || storeList.length === 0) {
        return stores;
    }

    return stores.filter(store => storeList.includes(store));
}

function resolveStore(stores) {
    const pickedStore = stores.find(store => store !== "GLOBAL" && locales[store]);

    if (pickedStore) {
        return pickedStore;
    }

    return DEFAULT_STORE;
}

function getAnyStoreSupportingLanguage(language) {
    if (!language) {
        return null;
    }

    return Object.keys(locales).find(store => store !== "GLOBAL" && locales[store] && locales[store][language]);
}

function getStoreLocale(store, language) {
    return locales[store] && locales[store][language] ? locales[store][language] : null;
}

function hasStoreLanguage(store, language) {
    return Boolean(getStoreLocale(store, language));
}

function findStoreWithLanguage(stores, language) {
    return stores.find(store => hasStoreLanguage(store, language)) || null;
}

function hasGlobalLanguage(language) {
    return Boolean(locales.GLOBAL && locales.GLOBAL[language]);
}

function isPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

function mergeLocaleObjects(baseLocale, overrideLocale) {
    if (!isPlainObject(baseLocale)) {
        return overrideLocale;
    }

    if (!isPlainObject(overrideLocale)) {
        return baseLocale;
    }

    const result = { ...baseLocale };

    Object.keys(overrideLocale).forEach(key => {
        if (isPlainObject(baseLocale[key]) && isPlainObject(overrideLocale[key])) {
            result[key] = mergeLocaleObjects(baseLocale[key], overrideLocale[key]);
            return;
        }

        result[key] = overrideLocale[key];
    });

    return result;
}

function resolveLocaleBundle(store, language) {
    const globalLocale = locales.GLOBAL && locales.GLOBAL[language];
    const storeLocale = getStoreLocale(store, language);

    if (globalLocale && storeLocale) {
        // Keep global language as base and allow store/country-specific keys to override.
        return mergeLocaleObjects(globalLocale, storeLocale);
    }

    return storeLocale || globalLocale || null;
}

function resolveStoreAndLanguage(stores, preferredLanguage, requestedCountry) {
    const currentStore = stores[0] || DEFAULT_STORE;

    // 1) Exact country/store + requested language.
    const requestedStore = findStoreWithLanguage(stores, preferredLanguage);
    if (requestedStore) {
        return { store: requestedStore, language: preferredLanguage };
    }

    // 2) Global requested language fallback.
    if (hasGlobalLanguage(preferredLanguage)) {
        return { store: currentStore, language: preferredLanguage };
    }

    // 3) Country/store + country default language.
    const countryDefaultLanguage = COUNTRY_DEFAULT_LANGUAGE[requestedCountry];
    const countryDefaultStore = countryDefaultLanguage ? findStoreWithLanguage(stores, countryDefaultLanguage) : null;
    if (countryDefaultStore) {
        return { store: countryDefaultStore, language: countryDefaultLanguage };
    }

    if (countryDefaultLanguage && hasGlobalLanguage(countryDefaultLanguage)) {
        return { store: currentStore, language: countryDefaultLanguage };
    }

    // 4) Country/store + EN fallback.
    const englishStore = findStoreWithLanguage(stores, DEFAULT_LANGUAGE);
    if (englishStore) {
        return { store: englishStore, language: DEFAULT_LANGUAGE };
    }

    if (hasGlobalLanguage(DEFAULT_LANGUAGE)) {
        return { store: currentStore, language: DEFAULT_LANGUAGE };
    }

    // 5) Any-store language fallback as last resort for backward compatibility.
    const anyStoreRequestedLanguage = getAnyStoreSupportingLanguage(preferredLanguage);
    if (anyStoreRequestedLanguage) {
        return { store: anyStoreRequestedLanguage, language: preferredLanguage };
    }

    // 6) Any-store EN fallback.
    const globalEnglishStore = getAnyStoreSupportingLanguage(DEFAULT_LANGUAGE) || DEFAULT_STORE;
    return { store: globalEnglishStore, language: DEFAULT_LANGUAGE };
}

export function resolveLocaleContext({ search, store, language, country, storeList, countryList }) {
    const query = parseLocaleQuery(search);

    if (!hasAnyLocaleInput({ query, store, language, country })) {
        const defaultLocale = resolveLocaleBundle(DEFAULT_STORE, DEFAULT_LANGUAGE) || locales[DEFAULT_STORE][DEFAULT_LANGUAGE];

        return {
            store: [DEFAULT_STORE],
            language: DEFAULT_LANGUAGE,
            country: STORE_TO_COUNTRY[DEFAULT_STORE] || null,
            locale: defaultLocale
        };
    }

    const requestedLanguage = normalizeLanguageToken(query.language || language) || DEFAULT_LANGUAGE;
    let requestedCountry = query.country || normalizeToken(country);

    const baseStores = unique([...getCountryStores(requestedCountry, requestedLanguage), ...query.stores, ...(store || [])]);

    const validStores = filterStoresByList(baseStores, storeList);
    const selectedStores = validStores.length > 0 ? validStores : [DEFAULT_STORE];
    const selectedStore = resolveStore(selectedStores);

    if (!requestedCountry) {
        requestedCountry = STORE_TO_COUNTRY[selectedStore] || null;
    }

    if (countryList && countryList.length > 0 && requestedCountry && !countryList.includes(requestedCountry)) {
        requestedCountry = null;
    }

    const resolved = resolveStoreAndLanguage([selectedStore, ...selectedStores], requestedLanguage, requestedCountry);
    const resolvedStore = resolved.store || selectedStore;
    const resolvedLanguage = resolved.language || DEFAULT_LANGUAGE;
    const resolvedLocale =
        resolveLocaleBundle(resolvedStore, resolvedLanguage) ||
        resolveLocaleBundle(DEFAULT_STORE, DEFAULT_LANGUAGE) ||
        locales[DEFAULT_STORE][DEFAULT_LANGUAGE];

    return {
        store: [resolvedStore],
        language: resolvedLanguage,
        country: requestedCountry,
        locale: resolvedLocale
    };
}

export function buildLocaleQuery({ store, language, country }) {
    const storeValue = Array.isArray(store) ? store.join(",") : store;
    const query = [];

    if (storeValue) {
        query.push(`store=${encodeURIComponent(storeValue)}`);
    }

    if (language) {
        query.push(`language=${encodeURIComponent(language)}`);
    }

    if (country) {
        query.push(`country=${encodeURIComponent(country)}`);
    }

    return query.join("&");
}
